import columnTypes from 'constants/columnTypes'
import notificationTypes from 'constants/notificationTypes'
import dayjs from 'dayjs'
import pretty from 'prettysize'
import { filter } from 'lodash'
import { useProfileImage } from '@/composables/useProfileImage.ts'
import 'dayjs/locale/de'

import { useClientStore } from '@/stores/system'

dayjs.locale('de')
export const getCheckOrCrossFromBool = (item) => {
	return {
		type: columnTypes.icon,
		value: item || isValidDate(item) ? 'fas fa-check' : 'fas fa-xmark',
		color: item || isValidDate(item) ? 'success' : 'error',
	}
}

export const parseRecordHpid = (item) => {
	return {
		type: columnTypes.text,
		value: item
			.split('-')
			.map((item) => parseInt(item, 10))
			.join('/'),
	}
}

export const getAccountHpId = (data) => {
	const clientStore = useClientStore()
	if (clientStore.isKarthago) {
		return {
			type: columnTypes.text,
			value: data ? data.split('_').pop() : '-',
		}
	}
	return {
		type: columnTypes.text,
		value: data ? data.substring(10).replace(/^0+/, '') : '-',
	}
}

/**
 * Konto-Label "Nummer - Bezeichnung", einheitlich wie in
 * templates/Autocomplete/Accounts.vue. Ohne Bezeichnung nur die Nummer.
 */
export const buildAccountLabel = (hpId, name) => {
	const number = getAccountHpId(hpId).value
	return name ? `${number} - ${name}` : number
}

/**
 * Datatable-Variante von buildAccountLabel.
 *
 * Der Wert kommt als `<hp_id> <name>` herein (pickKey-Array, Standard-keyJoin
 * Leerzeichen). Bewusst NICHT ueber formatWithItem geloest: useTableExport
 * kennt nur `format`, sonst stuende im CSV-Export die rohe hp_id.
 *
 * Getrennt wird am ERSTEN Leerzeichen. Das ist fuer BEIDE ERP-Typen sicher,
 * weil keine account_name.hp_id je ein Leerzeichen enthaelt:
 *   HausPerfekt/hpPsql: LPAD(fi_obj,6) || LPAD(bj,4) || LPAD(fi_ktonr,6)
 *                       -> 16 Stellen, reine Ziffern
 *                       (MobileAccountNamesViewMigration im Sync)
 *   Karthago:           {FirmenNr}_{Wija}_{ObjNr}_{KontoNr}
 *                       -> Ziffern und Unterstriche, z.B. 1_2026_20009_0008110
 * Die Nummernformatierung selbst bleibt bei getAccountHpId und damit
 * ERP-spezifisch (Karthago letztes _-Segment, sonst substring(10)).
 */
export const getAccountHpIdWithName = (data) => {
	if (!data) {
		return { type: columnTypes.text, value: '-' }
	}
	const index = data.indexOf(' ')
	const hpId = index === -1 ? data : data.slice(0, index)
	const name = index === -1 ? '' : data.slice(index + 1)

	return {
		type: columnTypes.text,
		value: buildAccountLabel(hpId, name),
	}
}

export const getLocalDateFromCarbon = (date) => ({
	type: columnTypes.text,
	value: isValidDate(date) ? dayjs(date).format('L') : '-',
})

// Renders a "max age in years" value (e.g. 5) together with the actual cutoff
// date the sync service will derive from it (1st of January, current year - N).
// Example: 5 → "5 (01.01.2021)". Used by the document_type table.
export const getMaxAgeYearsWithDate = (years) => {
	if (years === null || years === undefined || years === '') {
		return { type: columnTypes.text, value: '-' }
	}
	const n = Number(years)
	// UI clamps the input to 1-50, so anything below 1 (incl. 0) is invalid
	// and should render the empty placeholder instead of "0 (01.01.<year>)".
	if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
		return { type: columnTypes.text, value: '-' }
	}
	const cutoffYear = Math.max(1900, new Date().getFullYear() - n)
	const cutoffDate = dayjs(`${cutoffYear}-01-01`).format('L')
	return { type: columnTypes.text, value: `${n} (${cutoffDate})` }
}

export const getLocalDateTimeFromCarbon = (date) => ({
	type: columnTypes.text,
	value: isValidDate(date) ? dayjs(date).format('DD.MM.YYYY - HH:mm') : '-',
})

export const getReportStatusBadge = (status) => ({
	type: columnTypes.badge,
	value: {
		color: status.text_color,
		text: status.name,
	},
})

// Document types carry two name fields: `name` (sync-managed by the source ERP,
// for Karthago this is the raw folder path) and `display_name` (admin-set
// alias, Karthago-only). The API exposes a unified `display_label` which is
// `display_name || name`. UI surfaces should always use display_label so we
// never show the raw folder path when an admin has set a friendlier alias.
export const getDocumentTypeLabel = (documentType) => ({
	type: columnTypes.text,
	value: documentType?.display_label || documentType?.name || '-',
})

export const getUTCDateFromTimestamp = (timestamp) => ({
	type: columnTypes.text,
	value: isValidDate(timestamp)
		? dayjs.unix(timestamp).format('DD.MM.YYYY')
		: '-',
})

export const getTosAndPolicy = (tosAndPolicy: []) => {
	const match = tosAndPolicy?.toString().match(/true?/g || [])
	if (match === null || match?.length < 2) {
		return {
			type: columnTypes.tosAndPolicy,
			value: 'fas fa-exclamation',
		}
	} else {
		return {
			type: columnTypes.tosAndPolicy,
			value: '',
		}
	}
}

export const getColorBadge = (color) => ({
	type: columnTypes.badge,
	value: color,
})

export const getReviewStateIcon = (state) =>
	state === 'DENIED' || state === 'APPROVED'
		? getCheckOrCrossFromBool(state === 'APPROVED')
		: {
				type: columnTypes.icon,
				value: 'fal fa-hourglass-start',
				color: 'info',
			}
export const getPrettySize = (size) => {
	return {
		type: columnTypes.text,
		value: pretty(size),
	}
}

export const getMailHref = (mail) => ({
	type: columnTypes.html,
	value: `<a href="mailto:${mail}" style="text-decoration:none;color:inherit;cursor:pointer">${mail}</a>`,
})

export const getPhoneHref = (tel) => ({
	type: columnTypes.html,
	value: `<a href="tel:${tel}">${tel}</a>`,
})

export const getFixedFormat = (value, defaultValue = '-') => ({
	type: columnTypes.text,
	value:
		value && value !== '0' && !isNaN(Number(value.replace(',', '.')))
			? parseFloat(Number(value.replace(',', '.')).toFixed(2))
			: defaultValue,
})

export const getSigningType = (signeeType) => ({
	type: columnTypes.text,
	value: signeeType === 'MAIL' ? 'E-Mail' : 'Gerät',
})

export const getSigneeRole = (role) => ({
	type: columnTypes.text,
	value:
		role === 'WITNESS'
			? 'Zeuge'
			: role === 'TENANT'
				? 'Mieter'
				: 'Verwalter / Team',
})

export const getConsumptionValue = (data) => ({
	type: columnTypes.consumptionValue,
	value: data.value_end - data.value_start,
})

export const getCurrency = function (data) {
	const euro = new Intl.NumberFormat('de-DE', {
		style: 'currency',
		currency: 'EUR',
	})
	if (data === 0) {
		return {
			type: columnTypes.text,
			value: '-',
		}
	}
	if (typeof data === 'string') {
		data = getFixedFormat(data, 0).value
	}

	return {
		type: columnTypes.currency,
		value: euro.format(data),
	}
}

export const getNotificationType = (value, row = null) => {
	const notification = filter(
		notificationTypes,
		(item, key) => key === value ?? item
	)
	const unknown = { icon: 'far fa-sync', text: value }
	if (row !== null && Array.isArray(notification)) {
		switch (value) {
			case 'App\\Notifications\\ReviewDocumentNotification':
				return {
					type: columnTypes.iconText,
					value: {
						...notification[0],
						...{ text: row.data.template?.subject },
					},
				}
		}
	}
	return {
		type: columnTypes.iconText,
		value:
			Array.isArray(notification) && notification.length === 1
				? notification[0]
				: unknown,
	}
}

export const getDocumentIconFromValue = (value) => {
	if (Number.isNaN(value) || value === 0) {
		return {
			type: columnTypes.text,
			value: '',
		}
	} else {
		return {
			type: columnTypes.icon,
			value: 'fa-fw fas fa-file',
		}
	}
}

export const getDocumentContainerDialogIconFromValue = (value) => {
	if (Number.isNaN(value) || value === 0) {
		return {
			type: columnTypes.text,
			value: '',
		}
	} else {
		return {
			type: columnTypes.iconDocumentContainerDialog,
			value: 'fa-fw fas fa-file',
			id: value,
		}
	}
}

export const getUserImage = async (value: Api.User | Api.WhoAmI) => {
	if (value?.profile_image) {
		const { loadProfileImage, profileImage } = useProfileImage()
		await loadProfileImage(value)
		return {
			type: columnTypes.avatar,
			image: true,
			errorIcon: 'fas fa-image-circle-xmark',
			value: profileImage.value,
		}
	} else {
		return Promise.resolve({
			type: columnTypes.avatar,
			image: false,
			value: 'fa-fw fas fa-user',
		})
	}
}

export const getTextFromHtml = (value) => ({
	type: columnTypes.text,
	value: value.replace(/<[^>]*>?/gm, ''),
})

const isValidDate = (date) => date && !isNaN(new Date(date).getTime())

export const getHpIdColumn = (data: any) => {
	const clientStore = useClientStore()
	if (clientStore.isKarthago) {
		return {
			type: columnTypes.text,
			value: data ? data.split('_').pop() : '-',
		}
	} else {
		return {
			type: columnTypes.text,
			value: data ? data : '-',
		}
	}
}
export const getHpIdBuildingColumn = (hp_id) => {
	const clientStore = useClientStore()
	if (clientStore.isKarthago) {
		const data = hp_id.split('_')
		return {
			type: columnTypes.text,
			value: data ? `Firma: ${data[0]} ObjNr: ${data[1]}` : '-',
		}
	} else {
		return {
			type: columnTypes.text,
			value: hp_id ? hp_id : '-',
		}
	}
}

export const getHpId = (data) => {
	if (data === undefined || data === null) {
		return null
	}
	const clientStore = useClientStore()
	let prefix = 'HP-ID:'

	if (clientStore.isKarthago) {
		prefix = 'ID: '
		return `${prefix} ${data ? data.toString().split('_').pop() : '-'}`
	} else {
		return `${prefix} ${data ? data : '-'}`
	}
}

export const getHpIdRaw = () => {
	const clientStore = useClientStore()
	if (clientStore.isKarthago) {
		return 'ID:'
	} else {
		return 'HP-ID:'
	}
}

export const getFormatBytes = (bytes: number, decimals: number = 2): string => {
	if (bytes === 0) return '0 Bytes'

	const k = 1024
	const dm = decimals < 0 ? 0 : decimals
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

	const i = Math.floor(Math.log(bytes) / Math.log(k))

	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

export const getBuildingExportStatus = (status: Api.BuildingExportStatus) => {
	const statusConfig = {
		proposed: {
			icon: 'fas fa-check',
			color: 'success',
			tooltip: 'Marked for export',
		},
		not_proposed: {
			icon: 'fas fa-times',
			color: 'error',
			tooltip: 'Not marked for export',
		},
		missing_pm: {
			icon: 'fas fa-exclamation-triangle',
			color: 'warning',
			tooltip: 'Property manager missing',
		},
	}

	const config = statusConfig[status] || statusConfig.missing_pm

	return {
		type: columnTypes.icon,
		value: config.icon,
		color: config.color,
		tooltip: config.tooltip,
	}
}

export default {
	buildAccountLabel,
	getAccountHpId,
	getAccountHpIdWithName,
	getColorBadge,
	getCurrency,
	getCheckOrCrossFromBool,
	getDocumentIconFromValue,
	getFixedFormat,
	getLocalDateFromCarbon,
	getLocalDateTimeFromCarbon,
	getMaxAgeYearsWithDate,
	getMailHref,
	getPhoneHref,
	getReviewStateIcon,
	getSigneeRole,
	getSigningType,
	getTextFromHtml,
	getTosAndPolicy,
	getUTCDateFromTimestamp,
	getConsumptionValue,
	getNotificationType,
	getReportStatusBadge,
	getDocumentTypeLabel,
	getUserImage,
	getPrettySize,
	parseRecordHpid,
	getDocumentContainerDialogIconFromValue,
	getHpId,
	getHpIdColumn,
	getHpIdBuildingColumn,
	getHpIdRaw,
	getFormatBytes,
	getBuildingExportStatus,
}
